import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Trash2, Type, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { toast } from "@/hooks/use-toast";
import {
  BLOCK_IMAGE_ALLOWED_TYPES,
  BLOCK_IMAGE_MAX_BYTES,
  deleteBlockImageByPath,
  uploadBlockImage,
  type ComposerBlockImageAlign,
  type ComposerBlockImagePosition,
  type ComposerBlockImageSize,
} from "@/lib/composerBlocks";
import { cn } from "@/lib/utils";
import TypeSignatureDialog from "./TypeSignatureDialog";

type Props = {
  workspaceId: string;
  blockId: string;
  imageUrl: string | null;
  imagePath: string | null;
  imageAlt: string;
  imagePosition: ComposerBlockImagePosition;
  imageSize: ComposerBlockImageSize;
  imageAlign: ComposerBlockImageAlign;
  /** Block title — used as a sensible default for alt text on first upload. */
  blockTitle?: string;
  /** Signature blocks only — printed name used to seed the typed-signature dialog. */
  signatureName?: string;
  /** When true, render an inline error and red border on the alt input. */
  altError?: boolean;
  onChange: (next: {
    imageUrl: string | null;
    imagePath: string | null;
    imageAlt: string;
    imagePosition: ComposerBlockImagePosition;
    imageSize: ComposerBlockImageSize;
    imageAlign: ComposerBlockImageAlign;
  }) => void;
};

export const BlockImageField = ({
  workspaceId,
  blockId,
  imageUrl,
  imagePath,
  imageAlt,
  imagePosition,
  imageSize,
  imageAlign,
  blockTitle,
  signatureName,
  altError,
  onChange,
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const altRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [shouldFocusAlt, setShouldFocusAlt] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);

  // After a successful upload, focus the alt input once it renders.
  useEffect(() => {
    if (shouldFocusAlt && imageUrl && altRef.current) {
      altRef.current.focus();
      altRef.current.select();
      setShouldFocusAlt(false);
    }
  }, [shouldFocusAlt, imageUrl]);

  const pick = () => inputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!BLOCK_IMAGE_ALLOWED_TYPES.has(file.type)) {
      toast({ title: "Unsupported image", description: "Use PNG, JPEG, or WebP.", variant: "destructive" });
      return;
    }
    if (file.size > BLOCK_IMAGE_MAX_BYTES) {
      toast({ title: "Image too large", description: `Max ${BLOCK_IMAGE_MAX_BYTES / (1024 * 1024)} MB.`, variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      // Replace: best-effort delete old file.
      if (imagePath) await deleteBlockImageByPath(imagePath).catch(() => {});
      const up = await uploadBlockImage(workspaceId, blockId, file);
      // Pre-fill alt text with the block title so Save isn't silently blocked.
      const nextAlt = imageAlt.trim() ? imageAlt : (blockTitle ?? "").trim();
      onChange({
        imageUrl: up.signedUrl,
        imagePath: up.path,
        imageAlt: nextAlt,
        imagePosition,
        imageSize,
        imageAlign,
      });
      setShouldFocusAlt(true);
    } catch (err) {
      toast({ title: "Upload failed", description: (err as Error).message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (imagePath) await deleteBlockImageByPath(imagePath).catch(() => {});
    onChange({
      imageUrl: null,
      imagePath: null,
      imageAlt: "",
      imagePosition: "below",
      imageSize: "medium",
      imageAlign: "left",
    });
  };

  const onTypeInserted = (next: { imageUrl: string; imagePath: string; imageAlt: string }) => {
    onChange({
      imageUrl: next.imageUrl,
      imagePath: next.imagePath,
      imageAlt: next.imageAlt,
      imagePosition,
      imageSize,
      imageAlign,
    });
  };

  return (
    <div className="space-y-3 rounded-md border border-border/60 bg-muted/30 p-3">
      <div className="flex items-start gap-3">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border/60 bg-background">
          {imageUrl ? (
            <img src={imageUrl} alt={imageAlt || "Signature image"} className="h-full w-full object-contain" />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground/60" />
          )}
        </div>
        <div className="flex-1 space-y-2">
          <p className="text-xs text-muted-foreground">
            Optional image for this block (signature, headshot, author avatar, or product image). PNG, JPEG, or WebP, up to {BLOCK_IMAGE_MAX_BYTES / (1024 * 1024)} MB. An image needs alt text before the block can be saved.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={pick} disabled={busy}>
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1 h-3.5 w-3.5" />}
              {imageUrl ? "Replace image" : "Upload image"}
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setTypeOpen(true)} disabled={busy}>
              <Type className="mr-1 h-3.5 w-3.5" />
              Type with my font
            </Button>
            {imageUrl && (
              <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={onRemove} disabled={busy}>
                <Trash2 className="mr-1 h-3.5 w-3.5" />
                Remove
              </Button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={onFile}
          />
        </div>
      </div>

      {imageUrl && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={`block-img-alt-${blockId}`}>Alt text (required)</Label>
            <Input
              id={`block-img-alt-${blockId}`}
              ref={altRef}
              value={imageAlt}
              onChange={(e) => onChange({ imageUrl, imagePath, imageAlt: e.target.value, imagePosition, imageSize, imageAlign })}
              placeholder="Describe the image for screen readers (e.g., 'Handwritten signature: Jane Doe')"
              aria-invalid={altError ? true : undefined}
              className={cn(altError && "border-destructive focus-visible:ring-destructive")}
            />
            {altError && (
              <p className="text-[11px] text-destructive">
                Alt text is required when an image is attached. Add a short description for screen readers.
              </p>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Size</Label>
              <ToggleGroup
                type="single"
                value={imageSize}
                onValueChange={(v) => {
                  if (!v) return;
                  onChange({ imageUrl, imagePath, imageAlt, imagePosition, imageSize: v as ComposerBlockImageSize, imageAlign });
                }}
                className="justify-start"
              >
                <ToggleGroupItem value="small" size="sm">Small</ToggleGroupItem>
                <ToggleGroupItem value="medium" size="sm">Medium</ToggleGroupItem>
                <ToggleGroupItem value="large" size="sm">Large</ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="space-y-1.5">
              <Label>Alignment</Label>
              <ToggleGroup
                type="single"
                value={imageAlign}
                onValueChange={(v) => {
                  if (!v) return;
                  onChange({ imageUrl, imagePath, imageAlt, imagePosition, imageSize, imageAlign: v as ComposerBlockImageAlign });
                }}
                className="justify-start"
              >
                <ToggleGroupItem value="left" size="sm">Left</ToggleGroupItem>
                <ToggleGroupItem value="center" size="sm">Center</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Image position</Label>
            <ToggleGroup
              type="single"
              value={imagePosition}
              onValueChange={(v) => {
                if (!v) return;
                onChange({ imageUrl, imagePath, imageAlt, imagePosition: v as ComposerBlockImagePosition, imageSize, imageAlign });
              }}
              className="justify-start"
            >
              <ToggleGroupItem value="above" size="sm">Above text</ToggleGroupItem>
              <ToggleGroupItem value="below" size="sm">Below text</ToggleGroupItem>
              <ToggleGroupItem value="only" size="sm">Image only</ToggleGroupItem>
            </ToggleGroup>
            {imagePosition === "only" && (
              <p className="text-[11px] text-muted-foreground">
                Body text will be ignored when this block is inserted.
              </p>
            )}
          </div>
        </>
      )}

      <TypeSignatureDialog
        open={typeOpen}
        onOpenChange={setTypeOpen}
        workspaceId={workspaceId}
        blockId={blockId}
        blockTitle={blockTitle}
        signatureName={signatureName}
        onInserted={onTypeInserted}
      />
    </div>
  );
};

export default BlockImageField;
